import { useEffect, useRef } from "react";

/**
 * Watches for user activity (mouse, keyboard, scroll, touch, tab focus) and fires
 * callbacks after periods of inactivity.
 *
 * - onWarn  fires at `warnAfterMs` of inactivity (e.g. "are you still there?")
 * - onLock  fires at `lockAfterMs` of inactivity (hard lock; activity no longer revives)
 * - onResume fires when activity resumes AFTER a warning but BEFORE the lock
 *
 * Once locked, further activity is ignored — the caller is expected to require a
 * full reload / link reopen to recover.
 *
 * @param {object}   opts
 * @param {number}   opts.warnAfterMs  ms of inactivity before onWarn
 * @param {number}   opts.lockAfterMs  ms of inactivity before onLock
 * @param {Function} [opts.onWarn]
 * @param {Function} [opts.onLock]
 * @param {Function} [opts.onResume]
 * @param {boolean}  [opts.enabled=true]  disable the timer entirely (e.g. while submitting)
 */
export function useIdleTimer({
  warnAfterMs,
  lockAfterMs,
  onWarn,
  onLock,
  onResume,
  enabled = true,
}) {
  // Keep the latest callbacks in a ref so changing them doesn't re-subscribe listeners.
  const cbs = useRef({ onWarn, onLock, onResume });
  cbs.current = { onWarn, onLock, onResume };

  useEffect(() => {
    if (!enabled) return;

    let warnTimer;
    let lockTimer;
    let warned = false;
    let locked = false;

    const schedule = () => {
      warnTimer = setTimeout(() => {
        warned = true;
        cbs.current.onWarn?.();
      }, warnAfterMs);

      lockTimer = setTimeout(() => {
        locked = true;
        cbs.current.onLock?.();
      }, lockAfterMs);
    };

    const reset = () => {
      if (locked) return; // once locked, only a reload recovers
      clearTimeout(warnTimer);
      clearTimeout(lockTimer);
      if (warned) {
        warned = false;
        cbs.current.onResume?.();
      }
      schedule();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") reset();
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);

    schedule();

    return () => {
      clearTimeout(warnTimer);
      clearTimeout(lockTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, warnAfterMs, lockAfterMs]);
}
